<template>
  <v-container class="app-container">
  <div class="statistik-view">
    <!-- Hauptkarte mit Glasmorphismus-Effekt -->
    <v-card
      class="statistik-card"
      :class="{ 'maximized-card': isFormMaximized }"
    >
      <!-- Header mit Titel und Maximieren-Button -->
      <div class="statistik-header">
        <div class="title-container">
          <v-icon color="primary" size="24" class="mr-2"
            >mdi-chart-areaspline</v-icon
          >
          <h2 class="card-title">Statistik</h2>
        </div>
      </div>

      <v-divider class="card-divider"></v-divider>

      <!-- Tabs für verschiedene Zeiträume -->
      <div class="tabs-container">
        <div class="tab-navigation">
          <v-btn
            v-for="tab in tabs"
            :key="tab.value"
            :class="['tab-btn', { active: activeTab === tab.value }]"
            variant="text"
            @click="activeTab = tab.value"
          >
            <v-icon start size="small">{{ tab.icon }}</v-icon>
            {{ tab.text }}
          </v-btn>
        </div>
      </div>

      <!-- Filterbereich -->
      <div class="filter-section">
        <!-- Tägliche Filter -->
        <v-expand-transition>
          <div v-if="activeTab === 'day'" class="filter-container">
            <v-card class="filter-card">
              <div class="date-picker-wrapper">
                <v-menu
                  v-model="datePicker"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  location="bottom"
                >
                  <template v-slot:activator="{ props }">
                    <v-text-field
                      v-bind="props"
                      v-model="formattedDate"
                      label="Datum auswählen"
                      readonly
                      variant="outlined"
                      density="comfortable"
                      bg-color="rgba(30, 30, 40, 0.5)"
                      hide-details
                      prepend-inner-icon="mdi-calendar"
                      prepend-inner-icon-color="success"
                      class="date-field"
                      color="success"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="selectedDate"
                    @update:modelValue="updateDate"
                    class="date-picker"
                  ></v-date-picker>
                </v-menu>
              </div>

              <v-btn
                color="success"
                variant="elevated"
                class="filter-btn"
                @click="fetchStatistik"
              >
                <v-icon start>mdi-filter-variant</v-icon>
                Statistik anzeigen
              </v-btn>
            </v-card>
          </div>
        </v-expand-transition>

        <!-- Monatliche Filter -->
        <v-expand-transition>
          <div v-if="activeTab === 'month'" class="filter-container">
            <v-card class="filter-card">
              <div class="filter-inputs">
                <v-select
                  v-model="selectedMonth"
                  :items="monate"
                  item-title="text"
                  item-value="value"
                  label="Monat"
                  variant="outlined"
                  density="comfortable"
                  bg-color="rgba(30, 30, 40, 0.5)"
                  hide-details
                  class="filter-select"
                  prepend-inner-icon="mdi-calendar-month"
                  prepend-inner-icon-color="success"
                  color="success"
                ></v-select>

                <v-select
                  v-model="selectedYear"
                  :items="jahre"
                  label="Jahr"
                  variant="outlined"
                  density="comfortable"
                  bg-color="rgba(30, 30, 40, 0.5)"
                  hide-details
                  class="filter-select"
                  prepend-inner-icon="mdi-calendar-star"
                  prepend-inner-icon-color="success"
                  color="success"
                ></v-select>
              </div>

              <v-btn
                color="success"
                variant="elevated"
                class="filter-btn"
                @click="fetchStatistik"
              >
                <v-icon start>mdi-filter-variant</v-icon>
                Statistik anzeigen
              </v-btn>
            </v-card>
          </div>
        </v-expand-transition>

        <!-- Jährliche Filter -->
        <v-expand-transition>
          <div v-if="activeTab === 'year'" class="filter-container">
            <v-card class="filter-card">
              <div class="filter-inputs">
                <v-select
                  v-model="selectedYear"
                  :items="jahre"
                  label="Jahr"
                  variant="outlined"
                  density="comfortable"
                  bg-color="rgba(30, 30, 40, 0.5)"
                  hide-details
                  class="filter-select year-select"
                  prepend-inner-icon="mdi-calendar-star"
                  color="success"
                ></v-select>

                <div class="chart-type-toggle">
                  <span class="toggle-label">Diagrammtyp:</span>
                  <v-btn-toggle
                    v-model="yearlyChartType"
                    mandatory
                    density="comfortable"
                    color="success"
                    rounded
                  >
                    <v-btn value="bar" variant="outlined">
                      <v-icon>mdi-chart-bar</v-icon>
                      <span class="chart-type-text">Balken</span>
                    </v-btn>
                    <v-btn value="pie" variant="outlined">
                      <v-icon>mdi-chart-pie</v-icon>
                      <span class="chart-type-text">Kreis</span>
                    </v-btn>
                  </v-btn-toggle>
                </div>
              </div>

              <v-btn
                color="success"
                variant="elevated"
                class="filter-btn"
                @click="fetchStatistik"
              >
                <v-icon start>mdi-filter-variant</v-icon>
                Statistik anzeigen
              </v-btn>
            </v-card>
          </div>
        </v-expand-transition>
      </div>
    </v-card>

    <!-- Ergebniskarten mit Charts -->
    <div
      class="results-section"
      :class="{ 'maximized-results': isStatistikMaximized }"
    >
      <!-- Tägliche Statistik -->
      <v-expand-transition>
        <div v-if="activeTab === 'day' && shouldShowResults">
          <v-card
            class="results-card"
            :class="{ 'maximized-card': isStatistikMaximized }"
          >
            <div class="results-header">
              <div class="title-container">
                <v-icon color="primary" size="24" class="mr-2"
                  >mdi-calendar-today</v-icon
                >
                <h2 class="card-title">Tägliche Statistik</h2>
              </div>
            </div>

            <v-divider class="card-divider"></v-divider>

            <!-- Instandsetzungen Chart -->
            <div
              v-if="statistikErgebnisse.instandsetzungen.length > 0"
              class="chart-section"
            >
              <div class="chart-header">
                <v-icon color="success" class="mr-2">mdi-wrench</v-icon>
                <h3 class="chart-title">Instandsetzungen</h3>
              </div>
              <div class="chart-wrapper">
                <BarChart
                  :chart-data="chartDataDayInstandsetzung"
                  v-if="chartDataDayInstandsetzung.datasets[0].data.length > 0"
                />
                <div v-else class="no-data-message">
                  <v-icon size="36" color="grey" class="mb-2"
                    >mdi-chart-bar</v-icon
                  >
                  <p>Keine Daten für diesen Zeitraum vorhanden</p>
                </div>
              </div>
            </div>

            <!-- Retouren Chart -->
            <div
              v-if="statistikErgebnisse.retouren.length > 0"
              class="chart-section"
            >
              <div class="chart-header">
                <v-icon color="error" class="mr-2">mdi-package-variant</v-icon>
                <h3 class="chart-title">Retouren</h3>
              </div>
              <div class="chart-wrapper">
                <BarChart
                  :chart-data="chartDataDayRetouren"
                  v-if="chartDataDayRetouren.datasets[0].data.length > 0"
                />
                <div v-else class="no-data-message">
                  <v-icon size="36" color="grey" class="mb-2"
                    >mdi-chart-bar</v-icon
                  >
                  <p>Keine Daten für diesen Zeitraum vorhanden</p>
                </div>
              </div>
            </div>

            <!-- Zusammenfassungstabelle -->
            <div v-if="hasResults" class="summary-section">
              <div class="summary-header">
                <v-icon color="info" class="mr-2"
                  >mdi-information-outline</v-icon
                >
                <h3 class="summary-title">Zusammenfassung</h3>
              </div>
              <div class="summary-cards">
                <v-card class="summary-card instandsetzung-summary">
                  <div class="summary-icon">
                    <v-icon size="32" color="success">mdi-wrench</v-icon>
                  </div>
                  <div class="summary-content">
                    <div class="summary-label">Instandsetzungen</div>
                    <div class="summary-value">
                      {{ getSummaryCount("instandsetzungen") }}
                    </div>
                  </div>
                </v-card>

                <v-card class="summary-card retouren-summary">
                  <div class="summary-icon">
                    <v-icon size="32" color="error">mdi-package-variant</v-icon>
                  </div>
                  <div class="summary-content">
                    <div class="summary-label">Retouren</div>
                    <div class="summary-value">
                      {{ getSummaryCount("retouren") }}
                    </div>
                  </div>
                </v-card>
              </div>
            </div>
          </v-card>
        </div>
      </v-expand-transition>

      <!-- Monatliche Statistik -->
      <v-expand-transition>
        <div v-if="activeTab === 'month' && shouldShowResults">
          <v-card
            class="results-card"
            :class="{ 'maximized-card': isStatistikMaximized }"
          >
            <div class="results-header">
              <div class="title-container">
                <v-icon color="primary" size="24" class="mr-2"
                  >mdi-calendar-month</v-icon
                >
                <h2 class="card-title">Monatliche Statistik</h2>
              </div>
            </div>

            <v-divider class="card-divider"></v-divider>

            <!-- Instandsetzungen Chart -->
            <div
              v-if="statistikErgebnisse.instandsetzungen.length > 0"
              class="chart-section"
            >
              <div class="chart-header">
                <v-icon color="success" class="mr-2">mdi-wrench</v-icon>
                <h3 class="chart-title">Instandsetzungen</h3>
              </div>
              <div class="chart-wrapper">
                <BarChart
                  :chart-data="chartDataMonthInstandsetzung"
                  v-if="
                    chartDataMonthInstandsetzung.datasets[0].data.length > 0
                  "
                />
                <div v-else class="no-data-message">
                  <v-icon size="36" color="grey" class="mb-2"
                    >mdi-chart-bar</v-icon
                  >
                  <p>Keine Daten für diesen Zeitraum vorhanden</p>
                </div>
              </div>
            </div>

            <!-- Retouren Chart -->
            <div
              v-if="statistikErgebnisse.retouren.length > 0"
              class="chart-section"
            >
              <div class="chart-header">
                <v-icon color="error" class="mr-2">mdi-package-variant</v-icon>
                <h3 class="chart-title">Retouren</h3>
              </div>
              <div class="chart-wrapper">
                <BarChart
                  :chart-data="chartDataMonthRetouren"
                  v-if="chartDataMonthRetouren.datasets[0].data.length > 0"
                />
                <div v-else class="no-data-message">
                  <v-icon size="36" color="grey" class="mb-2"
                    >mdi-chart-bar</v-icon
                  >
                  <p>Keine Daten für diesen Zeitraum vorhanden</p>
                </div>
              </div>
            </div>

            <!-- Zusammenfassungstabelle -->
            <div v-if="hasResults" class="summary-section">
              <div class="summary-header">
                <v-icon color="info" class="mr-2"
                  >mdi-information-outline</v-icon
                >
                <h3 class="summary-title">Zusammenfassung</h3>
              </div>
              <div class="summary-cards">
                <v-card class="summary-card instandsetzung-summary">
                  <div class="summary-icon">
                    <v-icon size="32" color="success">mdi-wrench</v-icon>
                  </div>
                  <div class="summary-content">
                    <div class="summary-label">Instandsetzungen</div>
                    <div class="summary-value">
                      {{ getSummaryCount("instandsetzungen") }}
                    </div>
                  </div>
                </v-card>

                <v-card class="summary-card retouren-summary">
                  <div class="summary-icon">
                    <v-icon size="32" color="error">mdi-package-variant</v-icon>
                  </div>
                  <div class="summary-content">
                    <div class="summary-label">Retouren</div>
                    <div class="summary-value">
                      {{ getSummaryCount("retouren") }}
                    </div>
                  </div>
                </v-card>
              </div>
            </div>
          </v-card>
        </div>
      </v-expand-transition>

      <!-- Jährliche Statistik -->
      <v-expand-transition>
        <div v-if="activeTab === 'year' && shouldShowResults">
          <v-card
            class="results-card"
            :class="{ 'maximized-card': isStatistikMaximized }"
          >
            <div class="results-header">
              <div class="title-container">
                <v-icon color="primary" size="24" class="mr-2"
                  >mdi-calendar-star</v-icon
                >
                <h2 class="card-title">Jährliche Statistik</h2>
              </div>
            </div>

            <v-divider class="card-divider"></v-divider>

            <!-- Instandsetzungen Chart -->
            <div
              v-if="statistikErgebnisse.instandsetzungen.length > 0"
              class="chart-section"
            >
              <div class="chart-header">
                <v-icon color="success" class="mr-2">mdi-wrench</v-icon>
                <h3 class="chart-title">Instandsetzungen</h3>
              </div>
              <div class="chart-wrapper">
                <PieChart
                  v-if="yearlyChartType === 'pie'"
                  :chart-data="chartDataYearPieInstandsetzung"
                />
                <BarChart v-else :chart-data="chartDataYearBarInstandsetzung" />
              </div>
            </div>

            <!-- Retouren Chart -->
            <div
              v-if="statistikErgebnisse.retouren.length > 0"
              class="chart-section"
            >
              <div class="chart-header">
                <v-icon color="error" class="mr-2">mdi-package-variant</v-icon>
                <h3 class="chart-title">Retouren</h3>
              </div>
              <div class="chart-wrapper">
                <PieChart
                  v-if="yearlyChartType === 'pie'"
                  :chart-data="chartDataYearPieRetouren"
                />
                <BarChart v-else :chart-data="chartDataYearBarRetouren" />
              </div>
            </div>

            <!-- Zusammenfassungstabelle -->
            <div v-if="hasResults" class="summary-section">
              <div class="summary-header">
                <v-icon color="info" class="mr-2"
                  >mdi-information-outline</v-icon
                >
                <h3 class="summary-title">
                  Zusammenfassung {{ selectedYear }}
                </h3>
              </div>
              <div class="summary-cards">
                <v-card class="summary-card instandsetzung-summary">
                  <div class="summary-icon">
                    <v-icon size="32" color="success">mdi-wrench</v-icon>
                  </div>
                  <div class="summary-content">
                    <div class="summary-label">Instandsetzungen</div>
                    <div class="summary-value">
                      {{ getSummaryCount("instandsetzungen") }}
                    </div>
                  </div>
                </v-card>

                <v-card class="summary-card retouren-summary">
                  <div class="summary-icon">
                    <v-icon size="32" color="error">mdi-package-variant</v-icon>
                  </div>
                  <div class="summary-content">
                    <div class="summary-label">Retouren</div>
                    <div class="summary-value">
                      {{ getSummaryCount("retouren") }}
                    </div>
                  </div>
                </v-card>
              </div>
            </div>
          </v-card>
        </div>
      </v-expand-transition>

      <!-- Keine Daten -->
      <v-expand-transition>
        <v-card v-if="!hasResults && hasSearched" class="no-results-card">
          <div class="no-results-content">
            <v-icon size="64" color="grey" class="mb-4"
              >mdi-chart-areaspline</v-icon
            >
            <h3>Keine Daten verfügbar</h3>
            <p>
              Für den ausgewählten Zeitraum wurden keine Statistikdaten
              gefunden.
            </p>
            <v-btn
              color="success"
              variant="outlined"
              class="mt-4"
              @click="resetFilters"
            >
              <v-icon start>mdi-refresh</v-icon>
              Filter zurücksetzen
            </v-btn>
          </div>
        </v-card>
      </v-expand-transition>
    </div>

    <!-- Loading Overlay -->
    <div v-if="loading" class="loading-overlay">
      <v-progress-circular
        indeterminate
        color="success"
        size="64"
      ></v-progress-circular>
      <span class="loading-text">Daten werden geladen...</span>
    </div>
  </div>
</v-container>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import axios from "axios";
import BarChart from "@/components/charts/BarChart.vue";
import PieChart from "@/components/charts/PieChart.vue";
import { useAuthStore } from "@/stores/auth";
import { API_BASE_URL } from "../config";
import { useToast } from "vue-toastification";

// ======== Basis-Setup ========
const toast = useToast();
const authStore = useAuthStore();
const loading = ref(false);
const hasSearched = ref(false);

// ======== Tab-Navigation ========
const activeTab = ref("day");

// Tab-Konfiguration
const tabs = [
  { text: "Tägliche Statistik", value: "day", icon: "mdi-calendar-today" },
  { text: "Monatliche Statistik", value: "month", icon: "mdi-calendar-month" },
  { text: "Jährliche Statistik", value: "year", icon: "mdi-calendar-star" },
];

// ======== Filter-Zustände ========
// Tägliche Statistik
const datePicker = ref(false);
const selectedDate = ref(new Date());
const formattedDate = computed(() => formatDate(selectedDate.value));

// Monatliche Statistik
const selectedMonth = ref(String(new Date().getMonth() + 1).padStart(2, "0"));
const selectedYear = ref(new Date().getFullYear().toString());

// Jährliche Statistik
const yearlyChartType = ref("bar");

// ======== Auswahl-Optionen ========
const monate = [
  { text: "Januar", value: "01" },
  { text: "Februar", value: "02" },
  { text: "März", value: "03" },
  { text: "April", value: "04" },
  { text: "Mai", value: "05" },
  { text: "Juni", value: "06" },
  { text: "Juli", value: "07" },
  { text: "August", value: "08" },
  { text: "September", value: "09" },
  { text: "Oktober", value: "10" },
  { text: "November", value: "11" },
  { text: "Dezember", value: "12" },
];

const jahre = computed(() => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 10 }, (_, i) => (currentYear - i).toString());
});

// ======== Chart-Daten ========
// Tägliche Chart-Daten
const chartDataDayInstandsetzung = ref({
  labels: [],
  datasets: [
    {
      label: "Instandsetzungen",
      data: [],
      backgroundColor: "rgba(54, 162, 235, 0.8)",
      borderColor: "rgba(54, 162, 235, 1)",
      borderWidth: 1,
    },
  ],
});

const chartDataDayRetouren = ref({
  labels: [],
  datasets: [
    {
      label: "Retouren",
      data: [],
      backgroundColor: "rgba(255, 99, 132, 0.8)",
      borderColor: "rgba(255, 99, 132, 1)",
      borderWidth: 1,
    },
  ],
});

// Monatliche Chart-Daten
const chartDataMonthInstandsetzung = ref({
  labels: [],
  datasets: [
    {
      label: "Instandsetzungen",
      data: [],
      backgroundColor: "rgba(54, 162, 235, 0.8)",
      borderColor: "rgba(54, 162, 235, 1)",
      borderWidth: 1,
    },
  ],
});

const chartDataMonthRetouren = ref({
  labels: [],
  datasets: [
    {
      label: "Retouren",
      data: [],
      backgroundColor: "rgba(255, 99, 132, 0.8)",
      borderColor: "rgba(255, 99, 132, 1)",
      borderWidth: 1,
    },
  ],
});

// Jährliche Chart-Daten (Bar)
const chartDataYearBarInstandsetzung = ref({
  labels: [],
  datasets: [
    {
      label: "Instandsetzungen",
      data: [],
      backgroundColor: "rgba(54, 162, 235, 0.8)",
      borderColor: "rgba(54, 162, 235, 1)",
      borderWidth: 1,
    },
  ],
});

const chartDataYearBarRetouren = ref({
  labels: [],
  datasets: [
    {
      label: "Retouren",
      data: [],
      backgroundColor: "rgba(255, 99, 132, 0.8)",
      borderColor: "rgba(255, 99, 132, 1)",
      borderWidth: 1,
    },
  ],
});

// Jährliche Chart-Daten (Pie)
const chartDataYearPieInstandsetzung = ref({
  labels: [],
  datasets: [
    {
      data: [],
      backgroundColor: [],
      borderColor: Array(10).fill("rgba(255, 255, 255, 0.5)"),
      borderWidth: 1,
    },
  ],
});

const chartDataYearPieRetouren = ref({
  labels: [],
  datasets: [
    {
      data: [],
      backgroundColor: [],
      borderColor: Array(10).fill("rgba(255, 255, 255, 0.5)"),
      borderWidth: 1,
    },
  ],
});

// Hilfsarray für Pie-Farben mit optimierter Farbpalette
const pieColors = [
  "rgba(76, 175, 80, 0.8)", // Grün (Hauptfarbe)
  "rgba(255, 99, 132, 0.8)", // Rot für Retouren
  "rgba(255, 205, 86, 0.8)", // Gelb
  "rgba(46, 204, 113, 0.8)", // Hellgrün
  "rgba(39, 174, 96, 0.8)", // Mittelgrün
  "rgba(29, 131, 72, 0.8)", // Dunkelgrün
  "rgba(241, 90, 41, 0.8)", // Korall
  "rgba(142, 68, 173, 0.8)", // Lila
  "rgba(41, 128, 185, 0.8)", // Blau
  "rgba(26, 188, 156, 0.8)", // Türkis
];

// ======== Statistik-Ergebnisse ========
const statistikErgebnisse = ref({ instandsetzungen: [], retouren: [] });

// ======== Berechnete Eigenschaften ========
// Prüft, ob Ergebnisse vorhanden sind
const hasResults = computed(() => {
  return (
    statistikErgebnisse.value.instandsetzungen.length > 0 ||
    statistikErgebnisse.value.retouren.length > 0
  );
});

// Prüft, ob Ergebnisse angezeigt werden sollen
const shouldShowResults = computed(() => {
  return hasResults.value && hasSearched.value;
});

// ======== Hilfsfunktionen ========
// Formatiert ein Datum im Format tt-mm-yyyy
function formatDate(date) {
  if (!date) return "";
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

// Aktualisiert das Datum im "day"-Tab
function updateDate(date) {
  selectedDate.value = new Date(date);
  datePicker.value = false;
}

// Formatiert ein Datum für die Backend-API (yyyy-mm-dd)
function formatDateForBackend(date) {
  if (!date) return "";
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}`;
}

// Gibt die Gesamtanzahl von Elementen in einer Kategorie zurück
function getSummaryCount(category) {
  if (!statistikErgebnisse.value[category]) return 0;

  return statistikErgebnisse.value[category].reduce(
    (total, item) => total + Number(item.menge || item.anzahl || 0),
    0
  );
}

// Setzt Filter zurück
function resetFilters() {
  if (activeTab.value === "day") {
    selectedDate.value = new Date();
  } else if (activeTab.value === "month") {
    selectedMonth.value = String(new Date().getMonth() + 1).padStart(2, "0");
    selectedYear.value = new Date().getFullYear().toString();
  } else if (activeTab.value === "year") {
    selectedYear.value = new Date().getFullYear().toString();
  }

  fetchStatistik();
}

// ======== Chart-Daten Formatierung ========
// Format für tägliche Statistik
const formatChartDataDay = (data) => {
  chartDataDayInstandsetzung.value = {
    labels: data.instandsetzungen.map((item) => item.geraetetyp),
    datasets: [
      {
        label: "Instandsetzungen",
        data: data.instandsetzungen.map(
          (item) => item.menge || item.anzahl || 0
        ),
        backgroundColor: "rgba(76, 175, 80, 0.8)", // Grün statt Blau
        borderColor: "rgba(76, 175, 80, 1)", // Grün statt Blau
        borderWidth: 1,
        hoverBackgroundColor: "rgba(76, 175, 80, 1)", // Grün statt Blau
        borderRadius: 6,
      },
    ],
  };

  chartDataDayRetouren.value = {
    labels: data.retouren.map((item) => item.geraetetyp),
    datasets: [
      {
        label: "Retouren",
        data: data.retouren.map((item) => item.menge || item.anzahl || 0),
        backgroundColor: "rgba(255, 99, 132, 0.8)",
        borderColor: "rgba(255, 99, 132, 1)",
        borderWidth: 1,
        hoverBackgroundColor: "rgba(255, 99, 132, 1)",
        borderRadius: 6,
      },
    ],
  };
};

// Format für monatliche Statistik
const formatChartDataMonth = (data) => {
  chartDataMonthInstandsetzung.value = {
    labels: data.instandsetzungen.map((item) => item.geraetetyp),
    datasets: [
      {
        label: "Instandsetzungen",
        data: data.instandsetzungen.map(
          (item) => item.menge || item.anzahl || 0
        ),
        backgroundColor: "rgba(76, 175, 80, 0.8)", // Grün statt Blau
        borderColor: "rgba(76, 175, 80, 1)", // Grün statt Blau
        borderWidth: 1,
        hoverBackgroundColor: "rgba(76, 175, 80, 1)", // Grün statt Blau
        borderRadius: 6,
      },
    ],
  };

  chartDataMonthRetouren.value = {
    labels: data.retouren.map((item) => item.geraetetyp),
    datasets: [
      {
        label: "Retouren",
        data: data.retouren.map((item) => item.menge || item.anzahl || 0),
        backgroundColor: "rgba(255, 99, 132, 0.8)",
        borderColor: "rgba(255, 99, 132, 1)",
        borderWidth: 1,
        hoverBackgroundColor: "rgba(255, 99, 132, 1)",
        borderRadius: 6,
      },
    ],
  };
};

// Format für jährliche Statistik
const formatChartDataYear = (data) => {
  // Bar Chart für Instandsetzungen
  chartDataYearBarInstandsetzung.value = {
    labels: data.instandsetzungen.map((item) => item.geraetetyp),
    datasets: [
      {
        label: "Instandsetzungen",
        data: data.instandsetzungen.map(
          (item) => item.menge || item.anzahl || 0
        ),
        backgroundColor: "rgba(76, 175, 80, 0.8)", // Grün statt Blau
        borderColor: "rgba(76, 175, 80, 1)", // Grün statt Blau
        borderWidth: 1,
        hoverBackgroundColor: "rgba(76, 175, 80, 1)", // Grün statt Blau
        borderRadius: 6,
      },
    ],
  };

  // Bar Chart für Retouren
  chartDataYearBarRetouren.value = {
    labels: data.retouren.map((item) => item.geraetetyp),
    datasets: [
      {
        label: "Retouren",
        data: data.retouren.map((item) => item.menge || item.anzahl || 0),
        backgroundColor: "rgba(255, 99, 132, 0.8)",
        borderColor: "rgba(255, 99, 132, 1)",
        borderWidth: 1,
        hoverBackgroundColor: "rgba(255, 99, 132, 1)",
        borderRadius: 6,
      },
    ],
  };

  // Pie Chart: Für Instandsetzungen
  const labelsInstand = data.instandsetzungen.map((item) => item.geraetetyp);
  const dataInstand = data.instandsetzungen.map(
    (item) => item.menge || item.anzahl || 0
  );
  const bgColorsInstand = labelsInstand.map(
    (_, idx) => pieColors[idx % pieColors.length]
  );

  chartDataYearPieInstandsetzung.value = {
    labels: labelsInstand,
    datasets: [
      {
        data: dataInstand,
        backgroundColor: bgColorsInstand,
        borderColor: Array(labelsInstand.length).fill(
          "rgba(255, 255, 255, 0.5)"
        ),
        borderWidth: 1,
        hoverBackgroundColor: bgColorsInstand.map((color) =>
          color.replace("0.8", "1")
        ),
        hoverBorderWidth: 2,
        hoverOffset: 5,
      },
    ],
  };

  // Pie Chart: Für Retouren
  const labelsRetour = data.retouren.map((item) => item.geraetetyp);
  const dataRetour = data.retouren.map(
    (item) => item.menge || item.anzahl || 0
  );
  const bgColorsRetour = labelsRetour.map(
    (_, idx) => pieColors[idx % pieColors.length]
  );

  chartDataYearPieRetouren.value = {
    labels: labelsRetour,
    datasets: [
      {
        data: dataRetour,
        backgroundColor: bgColorsRetour,
        borderColor: Array(labelsRetour.length).fill(
          "rgba(255, 255, 255, 0.5)"
        ),
        borderWidth: 1,
        hoverBackgroundColor: bgColorsRetour.map((color) =>
          color.replace("0.8", "1")
        ),
        hoverBorderWidth: 2,
        hoverOffset: 5,
      },
    ],
  };
};

// ======== API-Interaktionen ========
// Statistikdaten vom Backend abrufen
const fetchStatistik = async () => {
  loading.value = true;

  // Alte Daten zurücksetzen
  statistikErgebnisse.value = { instandsetzungen: [], retouren: [] };

  // Alle Chart-Daten zurücksetzen
  resetChartData();

  const requestData = {
    user_id: authStore.user.id,
  };

  if (activeTab.value === "day") {
    requestData.type = "day";
    requestData.date = formatDateForBackend(selectedDate.value);
  } else if (activeTab.value === "month") {
    requestData.type = "month";
    requestData.month = selectedMonth.value;
    requestData.year = selectedYear.value;
  } else if (activeTab.value === "year") {
    requestData.type = "year";
    requestData.year = selectedYear.value;
  }

  try {
    const response = await axios.post(
      `${API_BASE_URL}statistik.php`,
      requestData,
      { withCredentials: true }
    );

    hasSearched.value = true;

    if (response.data.status === "success") {
      statistikErgebnisse.value = response.data.ergebnisse;

      if (activeTab.value === "day") {
        formatChartDataDay(response.data.ergebnisse);
      } else if (activeTab.value === "month") {
        formatChartDataMonth(response.data.ergebnisse);
      } else if (activeTab.value === "year") {
        formatChartDataYear(response.data.ergebnisse);
      }

      // Keine Toasts mehr, wenn Daten gefunden wurden - bessere UX
    } else {
      // Nur melden, wenn Fehler (nicht "keine Daten")
      if (response.data.status === "error") {
        toast.error(response.data.message || "Ein Fehler ist aufgetreten.");
      }
    }
  } catch (error) {
    console.error("Fehler beim Abrufen der Statistik", error);
    toast.error("Fehler beim Laden der Statistik!");
  } finally {
    loading.value = false;
  }
};

// Setzt alle Chart-Daten zurück
function resetChartData() {
  const emptyBarChart = {
    labels: [],
    datasets: [{ data: [], backgroundColor: [] }],
  };

  chartDataDayInstandsetzung.value = JSON.parse(JSON.stringify(emptyBarChart));
  chartDataDayRetouren.value = JSON.parse(JSON.stringify(emptyBarChart));
  chartDataMonthInstandsetzung.value = JSON.parse(
    JSON.stringify(emptyBarChart)
  );
  chartDataMonthRetouren.value = JSON.parse(JSON.stringify(emptyBarChart));
  chartDataYearBarInstandsetzung.value = JSON.parse(
    JSON.stringify(emptyBarChart)
  );
  chartDataYearBarRetouren.value = JSON.parse(JSON.stringify(emptyBarChart));
  chartDataYearPieInstandsetzung.value = JSON.parse(
    JSON.stringify(emptyBarChart)
  );
  chartDataYearPieRetouren.value = JSON.parse(JSON.stringify(emptyBarChart));
}

// ======== Lebenszyklus-Hooks & Watcher ========
// Bei Komponenten-Mount Statistik laden
onMounted(() => {
  fetchStatistik();
});

// Reagieren auf Tab-Wechsel oder Eingaben
watch([activeTab], () => {
  // Bei Tab-Wechsel immer neue Daten laden
  hasSearched.value = false;
  fetchStatistik();
});

// Selektiv neu laden bei Änderungen der Filter
watch(
  [selectedDate, selectedMonth, selectedYear, yearlyChartType],
  (newVal, oldVal) => {
    // Nur bei tatsächlichen Änderungen neu laden
    if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
      // Bei Änderung des Chart-Typs keine neue Abfrage erforderlich
      if (
        newVal[3] !== oldVal[3] &&
        newVal[0] === oldVal[0] &&
        newVal[1] === oldVal[1] &&
        newVal[2] === oldVal[2]
      ) {
        return;
      }
      fetchStatistik();
    }
  }
);
</script>

<style scoped>
/* ======== Grundlayout und Container ======== */
.app-container {
  max-width: 1200px !important;
  margin: 0 auto;
  padding-top: 24px;
  padding-bottom: 32px;
}

.statistik-view {
  position: relative;
  width: 100%;
}
/* ======== Karten mit Glasmorphismus-Effekt ======== */
.statistik-card,
.results-card,
.filter-card,
.no-results-card {
  background: rgba(25, 25, 30, 0.85) !important;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border-radius: 18px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  margin-bottom: 20px;
}

.statistik-card:hover,
.results-card:hover {
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.35);
  transform: translateY(-2px);
}

/* ======== Headers und Titel ======== */
.statistik-header,
.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  position: relative;
  z-index: 1;
}

.title-container {
  display: flex;
  align-items: center;
}

.card-title {
  font-size: 20px;
  font-weight: 500;
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0.3px;
  background: linear-gradient(45deg, #ffffff, rgba(255, 255, 255, 0.8));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.card-divider {
  border-color: rgba(255, 255, 255, 0.06);
  margin: 0;
}

/* ======== Maximieren-Button ======== */
.maximize-btn {
  opacity: 0.75;
  transition: all 0.2s ease;
  border-radius: 12px !important;
}

.maximize-btn:hover {
  opacity: 1;
  transform: translateY(-2px);
  background-color: rgba(76, 175, 80, 0.15) !important;
}

/* ======== Tabs-Navigation ======== */
.tabs-container {
  padding: 16px 20px;
}

.tab-navigation {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding: 4px;
  background: rgba(20, 20, 30, 0.5);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.tab-btn {
  padding: 8px 16px !important;
  border-radius: 8px !important;
  font-weight: 500;
  letter-spacing: 0.3px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  color: rgba(255, 255, 255, 0.7) !important;
}

.tab-btn.active {
  background: rgba(76, 175, 80, 0.25) !important;
  color: rgba(255, 255, 255, 1) !important;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.tab-btn:hover:not(.active) {
  background: rgba(255, 255, 255, 0.05) !important;
  transform: translateY(-2px);
}

/* ======== Filter-Bereich ======== */
.filter-section {
  padding: 0 20px 20px;
}

.filter-container {
  margin-bottom: 16px;
}

.filter-card {
  padding: 16px;
  background: rgba(30, 30, 40, 0.5) !important;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.filter-inputs {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.filter-select {
  min-width: 150px;
  flex-grow: 1;
}

.year-select {
  min-width: 100px;
}

.date-picker-wrapper {
  width: 100%;
}

.date-field {
  width: 100%;
}

.date-picker {
  background: rgba(30, 30, 40, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
}

.chart-type-toggle {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle-label {
  white-space: nowrap;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
}

.chart-type-text {
  margin-left: 4px;
  font-size: 0.85rem;
}

.filter-btn {
  margin-top: 8px;
  align-self: flex-end;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3) !important;
  letter-spacing: 0.5px;
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

.filter-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(76, 175, 80, 0.4) !important;
}

/* ======== Ergebnis-Bereich ======== */
.results-section {
  position: relative;
  transition: all 0.3s ease;
}

.maximized-results {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999;
  background: rgba(15, 15, 20, 0.5);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 20px;
  overflow-y: auto;
}

/* ======== Chart-Bereiche ======== */
.chart-section {
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.chart-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.chart-title {
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
}

.chart-wrapper {
  background: rgba(20, 20, 30, 0.3);
  padding: 16px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  margin: 0 auto;
  max-width: 800px;
  transition: all 0.3s ease;
}

.chart-wrapper:hover {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  transform: translateY(-2px);
}

/* ======== Zusammenfassungs-Bereich ======== */
.summary-section {
  padding: 20px;
}

.summary-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.summary-title {
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
}

.summary-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.summary-card {
  flex: 1;
  min-width: 200px;
  padding: 16px;
  border-radius: 12px;
  background: rgba(25, 25, 35, 0.5) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
}

.summary-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
}

.instandsetzung-summary {
  border-left: 4px solid rgba(76, 175, 80, 0.8);
}

.retouren-summary {
  border-left: 4px solid rgba(255, 99, 132, 0.8);
}

.summary-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(20, 20, 30, 0.5);
  width: 48px;
  height: 48px;
  border-radius: 12px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.summary-content {
  flex-grow: 1;
}

.summary-label {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 4px;
}

.summary-value {
  font-size: 1.5rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  letter-spacing: 0.5px;
}

/* ======== Leere Zustände ======== */
.no-data-message {
  padding: 40px 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
}

.no-data-message p {
  margin-top: 10px;
  font-size: 15px;
}

.no-results-card {
  padding: 0;
  max-width: 1200px;
  margin: 20px auto;
}

.no-results-content {
  padding: 40px 20px;
  text-align: center;
}

.no-results-content h3 {
  margin-top: 16px;
  margin-bottom: 8px;
  font-size: 18px;
  color: rgba(255, 255, 255, 0.8);
}

.no-results-content p {
  color: rgba(255, 255, 255, 0.6);
  max-width: 400px;
  margin: 0 auto;
}

/* ======== Loading-Overlay ======== */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(15, 15, 20, 0.7);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loading-text {
  margin-top: 16px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  font-weight: 500;
}

/* ======== Maximierter Zustand ======== */
.maximized-card {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  margin: 0;
  border-radius: 0;
  z-index: 999;
  overflow-y: auto;
  padding: 20px;
}

/* ======== Animationen ======== */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.chart-section,
.summary-section,
.filter-container {
  animation: fadeIn 0.3s ease;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

.filter-btn {
  animation: pulse 3s infinite;
}

/* ======== Responsive Design ======== */

/* Für Tablets und kleinere Desktops */
@media (max-width: 960px) {
  .filter-inputs {
    flex-direction: column;
    gap: 12px;
  }

  .chart-type-toggle {
    margin-top: 8px;
    flex-direction: column;
    align-items: flex-start;
  }

  .filter-btn {
    align-self: center;
    width: 100%;
    margin-top: 16px;
  }

  .summary-cards {
    flex-direction: column;
  }

  .summary-card {
    width: 100%;
  }
}

/* Für Mobilgeräte */
@media (max-width: 600px) {
  .statistik-view {
    padding-top: 16px;
  }

  .statistik-header,
  .results-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
    padding: 14px 16px;
  }

  .maximize-btn {
    position: absolute;
    top: 10px;
    right: 10px;
  }

  .tabs-container {
    padding: 12px;
  }

  .tab-navigation {
    flex-direction: column;
    gap: 4px;
  }

  .filter-section {
    padding: 0 12px 12px;
  }

  .chart-section {
    padding: 12px;
  }

  .chart-wrapper {
    padding: 8px;
  }

  .summary-section {
    padding: 12px;
  }

  .no-data-message,
  .no-results-content {
    padding: 30px 12px;
  }
}

/* ======== Scrollbar-Styling ======== */
.chart-wrapper::-webkit-scrollbar,
.results-section::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.chart-wrapper::-webkit-scrollbar-track,
.results-section::-webkit-scrollbar-track {
  background: transparent;
}

.chart-wrapper::-webkit-scrollbar-thumb,
.results-section::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
}

.chart-wrapper::-webkit-scrollbar-thumb:hover,
.results-section::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.25);
}

/* Im Stylesheet hinzufügen */
:deep(.v-field__prepend-inner .v-icon) {
  color: rgb(76, 175, 80) !important;
}
</style>
